import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, formatINR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Target, TrendUp, Percent, PencilSimple, Plus, Coins } from "@phosphor-icons/react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/**
 * Sales performance banner at the top of Daily Reports.
 * - Employees see own numbers (read-only).
 * - Admins can select any employee and set target + log daily turnover.
 */
export default function SalesPerformancePanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [employeeId, setEmployeeId] = useState("");
  const [salesEmployees, setSalesEmployees] = useState([]);
  const [data, setData] = useState(null);
  const [showTarget, setShowTarget] = useState(false);
  const [showTurnover, setShowTurnover] = useState(false);
  const [showAchievement, setShowAchievement] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/employees")
      .then(({ data }) => setSalesEmployees(data.filter((e) => (e.department || "").toLowerCase() === "sales")))
      .catch(() => { /* employees load failure handled by empty select */ });
  }, [isAdmin]);

  const load = useCallback(() => {
    const params = { year, month };
    if (isAdmin && employeeId) params.employee_id = employeeId;
    api.get("/sales/performance", { params })
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [year, month, employeeId, isAdmin]);

  useEffect(() => { load(); }, [load]);

  // Only show for Sales employees and for admin
  if (!isAdmin && String(user?.department || "").toLowerCase() !== "sales") return null;

  const perf = data?.performance_pct || 0;
  const perfColor = perf >= 100 ? "#10B981" : perf >= 60 ? "#FBBF24" : "#DC2626";
  const targetSelectedEmp = isAdmin ? employeeId : user?.id;
  const canManage = isAdmin && targetSelectedEmp;

  return (
    <div className="bg-white border border-[#E5E7EB]" data-testid="sales-perf-panel">
      <div className="px-4 sm:px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Sales Performance</div>
          <div className="font-display font-bold text-xl">{MONTHS[month - 1]} {year}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}
                  className="border border-[#E5E7EB] px-2 py-2 text-sm" data-testid="sp-month">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}
                  className="border border-[#E5E7EB] px-2 py-2 text-sm" data-testid="sp-year">
            {[today.getFullYear() + 1, today.getFullYear(), today.getFullYear() - 1, today.getFullYear() - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          {isAdmin && (
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
                    className="border border-[#E5E7EB] px-2 py-2 text-sm" data-testid="sp-employee">
              <option value="">— Select sales employee —</option>
              {salesEmployees.map((e) => <option key={e.id} value={e.id}>{e.employee_id} — {e.name}</option>)}
            </select>
          )}
          {canManage && (
            <>
              <button onClick={() => setShowTarget(true)} data-testid="sp-set-target-btn"
                      className="border border-[#106109] text-[#106109] px-3 py-2 text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-[#F0F7EF]">
                <PencilSimple size={14} /> Set Target
              </button>
              <button onClick={() => setShowAchievement(true)} data-testid="sp-edit-achievement-btn"
                      className="border border-[#1E40AF] text-[#1E40AF] px-3 py-2 text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-[#EFF6FF]">
                <Coins size={14} weight="fill" /> Edit Achievement
              </button>
              <button onClick={() => setShowTurnover(true)} data-testid="sp-log-turnover-btn"
                      className="brand-btn px-3 py-2 text-sm font-semibold inline-flex items-center gap-1.5">
                <Plus size={14} weight="bold" /> Log Turnover
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-[#E5E7EB]">
        <Cell label="Monthly Target" icon={Target} value={formatINR(data?.target_amount || 0)}
              tint="#106109" testId="sp-target" />
        <Cell label="Today's Turnover" icon={TrendUp} value={formatINR(data?.today_turnover || 0)}
              hint={data?.today ? data.today : ""} tint="#1E40AF" testId="sp-today" />
        <Cell label="MTD Turnover" icon={TrendUp} value={formatINR(data?.mtd_turnover || 0)}
              hint={`Days logged: ${data?.days?.length || 0}`} tint="#0F766E" testId="sp-mtd" />
        <PerfCell perf={perf} color={perfColor} target={data?.target_amount || 0} />
      </div>

      {isAdmin && !employeeId && (
        <div className="px-4 sm:px-6 py-3 text-xs text-[#6B7280] border-t border-[#E5E7EB]">
          Select a sales employee above to view / set their target and turnover.
        </div>
      )}

      {showTarget && (
        <TargetModal employee={salesEmployees.find((e) => e.id === employeeId)}
                     year={year} month={month} current={data?.target_amount || 0}
                     onClose={() => setShowTarget(false)}
                     onSaved={() => { setShowTarget(false); load(); }} />
      )}
      {showTurnover && (
        <TurnoverModal employee={salesEmployees.find((e) => e.id === employeeId)}
                       defaultDate={data?.today}
                       onClose={() => setShowTurnover(false)}
                       onSaved={() => { setShowTurnover(false); load(); }} />
      )}
      {showAchievement && (
        <AchievementModal
          employee={salesEmployees.find((e) => e.id === employeeId)}
          initialYear={year} initialMonth={month}
          currentAchieved={data?.mtd_turnover || 0}
          hasOverride={data?.achievement_override !== null && data?.achievement_override !== undefined}
          mtdFromDaily={data?.mtd_from_daily || 0}
          onClose={() => setShowAchievement(false)}
          onSaved={(y, m) => { setShowAchievement(false); if (y) setYear(y); if (m) setMonth(m); load(); }}
        />
      )}
    </div>
  );
}

function AchievementModal({ employee, initialYear, initialMonth, currentAchieved, hasOverride, mtdFromDaily, onClose, onSaved }) {
  const [y, setY] = useState(initialYear);
  const [m, setM] = useState(initialMonth);
  const [amount, setAmount] = useState(currentAchieved || 0);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!employee) { toast.error("Select an employee first"); return; }
    setSaving(true);
    try {
      await api.put("/sales/achievement", {
        employee_id: employee.id, year: y, month: m,
        achieved_amount: parseFloat(amount),
      });
      toast.success(`Achievement set for ${MONTHS[m - 1]} ${y}`);
      onSaved(y, m);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const clearOverride = async () => {
    if (!window.confirm(`Clear override and fall back to day-by-day sum (₹${mtdFromDaily}) for ${MONTHS[m - 1]} ${y}?`)) return;
    setSaving(true);
    try {
      await api.put("/sales/achievement", {
        employee_id: employee.id, year: y, month: m, achieved_amount: null,
      });
      toast.success("Override cleared");
      onSaved(y, m);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="sp-achievement-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-md border border-[#E5E7EB]">
        <div className="border-b border-[#E5E7EB] px-6 py-4">
          <div className="label-caps">Direct entry</div>
          <h3 className="font-display font-bold text-xl">Edit Achievement</h3>
          <div className="text-xs text-[#6B7280] mt-1">{employee?.name} ({employee?.employee_id})</div>
          <div className="text-xs text-[#6B7280] mt-1">Sets the total achieved figure for the selected month. Overrides the day-by-day sum.</div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-caps block mb-1.5">Month</label>
              <select value={m} onChange={(e) => setM(parseInt(e.target.value))}
                      data-testid="sp-ach-month" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm">
                {MONTHS.map((mm, i) => <option key={mm} value={i + 1}>{mm}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1.5">Year</label>
              <input type="number" value={y} onChange={(e) => setY(parseInt(e.target.value))}
                     data-testid="sp-ach-year" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="label-caps block mb-1.5">Total achieved (₹)</label>
            <input type="number" min="0" step="1" required value={amount}
                   onChange={(e) => setAmount(e.target.value)} data-testid="sp-ach-amount"
                   className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
            <div className="text-[11px] text-[#6B7280] mt-1">
              Day-by-day sum currently on record: ₹{mtdFromDaily.toLocaleString("en-IN")}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 text-sm border border-[#E5E7EB]">Cancel</button>
            {hasOverride && (
              <button type="button" onClick={clearOverride} disabled={saving} data-testid="sp-ach-clear"
                      className="py-2.5 px-3 text-sm border border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2]">Clear override</button>
            )}
            <button type="submit" disabled={saving} data-testid="sp-ach-save"
                    className="brand-btn flex-1 py-2.5 text-sm font-semibold">{saving ? "…" : "Save achievement"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Cell({ label, icon: Icon, value, hint, tint, testId }) {
  return (
    <div className="p-4 sm:p-5" data-testid={testId}>
      <div className="flex items-center gap-2">
        <Icon size={16} color={tint} weight="fill" />
        <div className="label-caps">{label}</div>
      </div>
      <div className="mt-2 font-display font-black text-xl sm:text-2xl tracking-tight break-words">{value}</div>
      {hint && <div className="text-xs text-[#6B7280] mt-1">{hint}</div>}
    </div>
  );
}

function PerfCell({ perf, color, target }) {
  const capped = Math.min(perf, 100);
  return (
    <div className="p-4 sm:p-5" data-testid="sp-perf">
      <div className="flex items-center gap-2">
        <Percent size={16} color={color} weight="fill" />
        <div className="label-caps">Performance</div>
      </div>
      <div className="mt-2 font-display font-black text-xl sm:text-2xl tracking-tight" style={{ color }}>
        {target > 0 ? `${perf.toFixed(1)}%` : "—"}
      </div>
      <div className="mt-2 h-2 w-full bg-[#F3F4F6] overflow-hidden">
        <div className="h-full transition-all duration-500" style={{ width: `${capped}%`, background: color }} />
      </div>
      <div className="text-xs text-[#6B7280] mt-1">
        {target > 0 ? (perf >= 100 ? "Target achieved 🎯" : `${(100 - capped).toFixed(1)}% to go`) : "Target not set"}
      </div>
    </div>
  );
}

function TargetModal({ employee, year, month, current, onClose, onSaved }) {
  const [amount, setAmount] = useState(current || 0);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!employee) { toast.error("Select an employee first"); return; }
    setSaving(true);
    try {
      await api.put("/sales/target", { employee_id: employee.id, year, month, target_amount: parseFloat(amount) });
      toast.success("Target updated");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="sp-target-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-md border border-[#E5E7EB]">
        <div className="border-b border-[#E5E7EB] px-6 py-4">
          <div className="label-caps">Sales · {MONTHS[month - 1]} {year}</div>
          <h3 className="font-display font-bold text-xl">Set Monthly Target</h3>
          <div className="text-xs text-[#6B7280] mt-1">{employee?.name} ({employee?.employee_id})</div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label-caps block mb-1.5">Target amount (₹)</label>
            <input type="number" min="0" step="1000" required value={amount}
                   onChange={(e) => setAmount(e.target.value)} data-testid="sp-target-amount"
                   className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 text-sm border border-[#E5E7EB]">Cancel</button>
            <button type="submit" disabled={saving} data-testid="sp-target-save"
                    className="brand-btn flex-1 py-2.5 text-sm font-semibold">{saving ? "…" : "Save target"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

function TurnoverModal({ employee, defaultDate, onClose, onSaved }) {
  const [date, setDate] = useState(defaultDate || new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!employee) { toast.error("Select an employee first"); return; }
    setSaving(true);
    try {
      await api.post("/sales/turnover", { employee_id: employee.id, date, amount: parseFloat(amount), notes });
      toast.success("Turnover logged");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="sp-turnover-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-md border border-[#E5E7EB]">
        <div className="border-b border-[#E5E7EB] px-6 py-4">
          <div className="label-caps">Turnover entry</div>
          <h3 className="font-display font-bold text-xl">Log Business Turnover</h3>
          <div className="text-xs text-[#6B7280] mt-1">{employee?.name} ({employee?.employee_id})</div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-caps block mb-1.5">Date</label>
              <input type="date" required value={date} onChange={(e) => setDate(e.target.value)}
                     data-testid="sp-turnover-date" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="label-caps block mb-1.5">Amount (₹)</label>
              <input type="number" min="0" step="1" required value={amount} onChange={(e) => setAmount(e.target.value)}
                     data-testid="sp-turnover-amount" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="label-caps block mb-1.5">Notes (optional)</label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                      className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
          </div>
          <div className="text-xs text-[#6B7280]">Submitting the same date again will overwrite the previous entry.</div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 text-sm border border-[#E5E7EB]">Cancel</button>
            <button type="submit" disabled={saving} data-testid="sp-turnover-save"
                    className="brand-btn flex-1 py-2.5 text-sm font-semibold">{saving ? "…" : "Save turnover"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
